import React, { useState, useReducer, useEffect, useMemo, useRef } from 'react';
import moment from 'moment';
import { useIntl, history, getLocale } from 'umi';
import { Table } from 'tea-component';
import Filters from './components/Filters';
import StatsLineChart from './components/LineChart';
import CardSlotV2 from '@/pages/intelligence/components/CardSlotV2';
import DynamicTable from '../../components/DynamicColTable/Table';
import GenreProfileSearch from './components/Search';
import { dbiPageLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import { buriedPointPage } from '@/pages/home/utils/buriedPoint';
import {
  IStatsProps,
  IAction,
  IEntityType,
  IStatsFilter,
  IStatsModifiedParams,
  IDimension,
  IGranularity,
} from '../../typings';
import { GRANULARITY_MAP } from '../../const';
import { transGenreSource } from '../../utils';
import { AreaItemtype } from '@/pages/intelligence/components/Filter/util';
import {
  MARKET_DIMENSION_CONFIG,
  METRIC_KEY_MAP,
  PAGE_LOG_CONFIG_KEYS,
} from './config';
import { isEmpty } from '@/pages/intelligence/utils/commonUtils';
import {
  getGenreProfileStatsTableData,
  getGenreProfileStatsLineData,
  getGenreProfilePageDataMobile,
  getGenreProfileImgDataMobile,
  getGenreProfileStatsLine,
  // getGenreProfileRegionAndCountriesListWhenOther,
  // getGenreProfileRegionAndCountriesListWhenTotalAndMobile,
} from '@/pages/intelligence/services/genreProfile';
import useMarketFilterApi, {
  MarketSpecailType,
} from '@/pages/intelligence/hooks/useMarketSelector';
import {
  getDefulatTimeValueByDateType,
  toDateTypeFilter,
} from '@/pages/intelligence/utils/common';
import {
  translateStateParams,
  translateStateListFunc,
  translateStateResponse,
} from './adapter';
import useGenreSelector from '@/pages/intelligence/hooks/useGenreSelector';
import useCountriesNames from '@intelligence/hooks/useCountriesNames';

const { pageable } = Table.addons;

const reducer = (state: IStatsFilter, { type, payload }: IAction) => {
  return {
    ...state,
    [type]: payload,
  };
};

const MAX_CHECKED = 10;

function dealMap(
  data: any,
  type: string,
  mainGenre?: string,
  subGenre?: string,
) {
  if (!data) return {};
  const targentData = type
    ? type === 'iegg' || type === 'ieg'
      ? data.ieg
      : data.annir
    : data;
  const map: any = {};
  // 处理数组：转换内部元素
  if (Array.isArray(targentData)) {
    targentData.forEach((item: any) => {
      const key: string = type ? item.main_genre.genre : item.genre;
      map[key] = type ? item.main_genre : item;
      type && (map[key].subGenres = item.sub_genre);
    });
  }
  if (!subGenre) {
    return subGenre === '' ? {} : map[mainGenre];
  }
  if (mainGenre === '') {
    let cdatas = '';
    Object.keys(map).forEach((key) => {
      dealMap(map[key]?.subGenres, '', subGenre) &&
        (cdatas = dealMap(map[key]?.subGenres, '', subGenre));
    });
    if (cdatas) {
      return cdatas;
    }
  }
  return dealMap(map[mainGenre]?.subGenres, '', subGenre) || {};
}

export default function Stats(props: IStatsProps) {
  const {
    isMarketSegmentHide,
    entityType,
    genreType,
    genreName,
    genreSource,
    filter,
    filterDispatch,
    leftSegment = '',
    loading,
    loadingDispatch,
  } = props;
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const { findCountryObj } = useCountriesNames();
  const [regionOpts, setRegionOpts] = useState<AreaItemtype[]>([]);
  // 使用统一全局存储数据
  const { genreOptions } = useGenreSelector({});
  const [tableData, setTableData] = useState([]);
  // 用于搜索后重置
  const [originalTableData, setOriginalTableData] = useState([]);
  const [originalPageData, setOriginalPageData] = useState({
    total: 0,
    page: 1,
    pageSize: 50,
  });
  const [lineChartData, setLineChartData] = useState({});
  const [requestParams, setRequestParams] = useState([]);
  const [originalLineData, setOriginalLineData] = useState([]);
  const [lineChartSumData, setLineChartSumData] = useState([]);
  // 页面打点
  const [pageLogInfo, setPageLogInfo] = useState<any>({});

  const [metricsList, setMetricsList] = useState([]);

  const { filterLoading: filterLoadingPC, regionOptions: regionOptionsPC } =
    useMarketFilterApi({
      filterType: MarketSpecailType.digitalRevenue,
    });
  const {
    filterLoading: filterLoadingMobile,
    regionOptions: regionOptionsMobile,
  } = useMarketFilterApi({
    filterType: MarketSpecailType.common,
  });
  /*
  const [filter, filterDispatch] = useReducer(reducer, {
    marketType: entityType,
    dimension: 'operation',
    regions: ['global'],
    markets: ['global'],
    startDate: '',
    endDate: '',
    granularity: 'monthly',
  });*/
  const lastMarketType = useRef<any>(null);
  const lastDimens = useRef<any>(null);
  // 缓存最后一次组装接口请求入参的结果，用于搜索（由于搜索组件内 有useEffect导致 获取不到最新的参数，需要用这个ref来实现获取到最新的参数）
  const cachedParams = useRef<any>({});

  // 获取当前页面的配置文件
  const getCurPageConfig = ({
    marketType,
    dimension,
    dataSourceSingle,
  }: {
    marketType?: IEntityType;
    dimension?: IDimension;
    dataSourceSingle?: string;
  }) => {
    const marketPage =
      MARKET_DIMENSION_CONFIG[marketType || filter.marketType || 'mobile'];
    const _dimen = dimension || filter.dimension || 'games';
    return {
      title: marketPage.title?.[_dimen],
      lineMetricList: marketPage.lineMetricList?.[_dimen],
      lineTotalText: marketPage.lineTotalText?.[_dimen],
      lineMetricInfo: null,
      tableMetricList: marketPage.tableMetricList(dataSourceSingle)?.[_dimen],
      tableFirstCol: marketPage.firstCol?.[_dimen],
      tableSpecialCol: marketPage.specialCol?.[_dimen],
      tableFormatter: marketPage.tableFormatter?.[_dimen],
      lineFormatter: marketPage.lineFormatter?.[_dimen],
      keyMap: marketPage.keyMap?.[_dimen],
      regionMetricType: marketPage.regionMetricType?.[_dimen],
    };
  };

  // 当前页面配置文件的缓存，有异步问题
  const curPageConfig = useMemo(() => {
    return getCurPageConfig({
      dataSourceSingle: filter.dataSourceSingle.value,
    });
  }, [filter.marketType, filter.dimension, filter.dataSourceSingle.value]);

  const tableScrollWidth = useMemo(() => {
    return filter.dimension === 'games'
      ? filter.marketType === 'mobile'
        ? 2200
        : 1850
      : 1100;
  }, [filter.marketType, filter.dimension]);

  const [tableAction, tableActionDispatch] = useReducer(reducer, {
    total: 0,
    pageIndex: 1,
    pageSize: 50,
    sortKey: '',
    sortKeyLabel: '',
    sortOrder: 'desc',
    selectedList: [],
    SelectionAll: [],
  });

  // 搜索列表点击某条数据, byGame: 填入id; byCountry: 填入name_abbr; byCompany: 填入name
  const getCheckedCode = (list, modifiedParams) => {
    const _dimen = modifiedParams?.data_tag || filter.dimension || 'operation';
    const keyMap = curPageConfig.keyMap() || {};
    let targetKey = keyMap.id || 'id';
    switch (_dimen) {
      case 'countries':
        targetKey = keyMap.abbr;
        break;
      case 'companies':
        targetKey = keyMap.id;
      default:
        break;
    }
    return list?.map((item) => item[targetKey]);
  };

  // 日期组件返回的endDate是月最后一天，组件判断变化的时候会出现错误，所以要先转成月第一天
  const getFirstDayOfMonth = (date: string): string => {
    const m = date ? moment(date) : moment();
    return m?.startOf('month')?.format('YYYY-MM-DD');
  };

  // 获取参数，modifiedParams是异步时传过来的参数
  const getParams = ({
    type,
    modifiedParams,
  }: {
    type: 'table' | 'line' | 'operation';
    modifiedParams?: IStatsModifiedParams;
  }) => {
    if (!modifiedParams) modifiedParams = {};
    const _curPageConfig = getCurPageConfig({
      marketType: modifiedParams.entity_type,
      dimension: modifiedParams.data_tag,
    });
    const granularity =
      modifiedParams.granularity || filter.granularity || 'monthly';
    const dimension =
      modifiedParams.data_tag || filter.dimension || 'operation';
    // 根据粒度过滤au字段
    const getMetric = (
      _metricList: any[],
      _granularity: IGranularity,
      _dimension: IDimension,
    ) => {
      const list: string[] = [];
      _metricList?.forEach((item: any) => {
        const _metric = item.metric;
        if (_dimension !== 'games') {
          list.push(_metric);
        } else {
          if (_metric.includes(GRANULARITY_MAP[_granularity])) {
            list.push(_metric);
          } else if (!_metric.includes('au')) {
            list.push(_metric);
          }
        }
      });
      return list;
    };
    const params = {
      regions: filter.markets,
      regionType: 'market',
      entity_type: filter.marketType,
      data_tag: filter.dimension,
      granularity: filter.granularity,
      genre_tab: genreType,
      genre: genreName,
      genre_source: transGenreSource(genreSource),
      start_date: filter.startDate,
      end_date: filter.endDate,
      dataSourceSingle:
        typeof filter.dataSourceSingle === 'object'
          ? filter.dataSourceSingle.value
          : filter.dataSourceSingle,
    };
    const lineParams = {
      ...params,
      // order: tableAction.sortOrder,
      // 后端通过这个字段去控制折线图返回多少条，表格返回多少指标
      // metrics: getMetric(_curPageConfig.tableMetricList, granularity, dimension),
      // 在折线图中，select_code用于表示选中多少条
      // select_code: modifiedParams?.checkedList
      //   ? getCheckedCode(modifiedParams?.checkedList, modifiedParams)
      //   : getCheckedCode(tableAction.selectedList, modifiedParams),
    };

    const tableParams = {
      ...lineParams,
      page: tableAction.pageIndex,
      page_size: tableAction.pageSize,
      keyword: '',
      select_code: modifiedParams?.select_code
        ? modifiedParams?.select_code
        : [],
    };
    setMetricsList(
      _curPageConfig.tableMetricList?.map((item: any) => item.metric) || [],
    );
    const getParamsByType = (_type: 'table' | 'line' | 'operation') => {
      switch (_type) {
        case 'table':
          return tableParams;
        case 'line':
          return lineParams;
        default:
          return params;
      }
    };
    const finalParams = Object.assign(getParamsByType(type), modifiedParams);
    // 表格的请求参数中，select_code是不用的，会影响请求结果
    if (type === 'table') {
      // if (finalParams.hasOwnProperty('select_code')) {
      //   finalParams.select_code = [];
      // }
      // 每次重新组装接口入参后，更新缓存的接口入参，用于搜索时获取页面参数用
      cachedParams.current = finalParams;
    }
    return finalParams;
  };

  const setTableLoading = (payload: boolean) => {
    loadingDispatch({ type: 'table', payload: !!payload });
  };

  // 搜索时，获取页面的参数
  const getParamsForSearch = (inputValue: any, type: string) => {
    // const add =
    //   type === 'search'
    //     ? {
    //         keyword: inputValue,
    //       }
    //     : {
    //         select_code: [inputValue],
    //       };
    const params = Object.assign({}, cachedParams.current, inputValue);
    return params;
  };

  // 对数组进行排序
  const getSortedTableData = ({
    list,
    sortKey,
    sortOrder,
    sortPosition,
    matchKey,
    valueType,
    pageConfig,
  }: {
    list: any;
    sortKey?: string;
    sortOrder?: string;
    sortPosition: 'main' | 'other';
    matchKey: string;
    valueType: 'date' | 'other';
    pageConfig: any;
  }) => {
    if (!sortKey) sortKey = tableAction.sortKey;
    if (!sortOrder) sortOrder = tableAction.sortOrder;
    const len = sortKey?.length;
    // 获取类型，是值还是环比值
    const type = sortKey?.slice(len - 1);
    // 从传入的list中获取对比的值，当sortPosition为main的时候，从list的第一层中找，否则从metricList中找
    const getTargetValue = (sortItem: any) => {
      const keyMap = pageConfig?.keyMap();
      const metricListKey = keyMap?.metricList || 'metrics';
      const metricKey = keyMap?.metricName || 'metrics';
      // 根据类型判断从metricValue还是metricRatio中取值
      const typeStr = type === '0' ? keyMap?.metricValue : keyMap?.metricRatio;
      const targetSortKey = sortKey?.slice(0, len - 2);
      return sortPosition === 'main'
        ? sortItem[matchKey]
        : sortItem[metricListKey]?.find(
            (item: any) => item[metricKey] === targetSortKey,
          )?.[typeStr];
    };
    // 过滤方法，主要作用是将环比（dod）为空的和不为空的拆分不同数组
    const filter = () => {
      const keyMap = pageConfig?.keyMap();
      const metricListKey = keyMap?.metricList || 'metrics';
      const metricKey = keyMap?.metricName || 'metrics';
      const metricRatio = keyMap?.metricRatio || 'dod';
      const isDodNullArr: any[] = [];
      const isDodNotNullArr: any[] = [];
      list?.[metricListKey]?.forEach((item: any) => {
        const _ratio = item?.[metricKey]?.[metricRatio];
        if (_ratio === null) {
          isDodNullArr.push(item);
        } else {
          isDodNotNullArr.push(item);
        }
      });
      return {
        isDodNullArr,
        isDodNotNullArr,
      };
    };
    // 对比方法
    const compareFun = (a: any, b: any) => {
      let aValue = getTargetValue(a);
      let bValue = getTargetValue(b);
      if (valueType === 'date') {
        aValue = moment(aValue);
        bValue = moment(bValue);
      }
      return sortOrder === 'desc' ? bValue - aValue : aValue - bValue;
    };
    // 如果是以值排序或者没有环比数据时，直接使用sort方法
    if (type === '0' || type === '1') {
      return list.sort(compareFun);
    }
    // 如果是以环比排序，需要拼接数组
    const { isDodNullArr, isDodNotNullArr } = filter();
    return isDodNotNullArr.sort(compareFun).concat(...isDodNullArr);
  };

  // 筛选器改变时候的回调
  const handleFilterChange = ({ type, payload }: any) => {
    let modifiedParams = {};
    // 在切换市场类型或者维度后，要重置选中的code
    if (['marketType', 'dimension'].includes(type)) {
      modifiedParams = {
        select_code: [],
      };
    }
    if (type === 'cascade') {
      const {
        start_date,
        end_date,
        granularity,
        regions,
        markets,
        dataSourceSingle,
      } = payload;
      const _startDate =
        granularity === 'monthly' ? getFirstDayOfMonth(start_date) : start_date;
      const _endDate =
        granularity === 'monthly' ? getFirstDayOfMonth(end_date) : end_date;
      if (filter.startDate !== _startDate) {
        filterDispatch({ type: 'startDate', payload: _startDate });
        Object.assign(modifiedParams, {
          [METRIC_KEY_MAP.rangeTime.startDate]: _startDate,
        });
      }
      if (filter.endDate !== _endDate) {
        filterDispatch({ type: 'endDate', payload: _endDate });
        Object.assign(modifiedParams, {
          [METRIC_KEY_MAP.rangeTime.endDate]: _endDate,
        });
      }
      if (JSON.stringify(filter.markets) !== JSON.stringify(markets)) {
        filterDispatch({ type: 'markets', payload: markets });
        filterDispatch({ type: 'regions', payload: regions });
        Object.assign(modifiedParams, {
          [METRIC_KEY_MAP.region]: markets,
        });
      }
      // filter.granularity = payload.granularity;
      if (filter.granularity !== granularity) {
        filterDispatch({ type: 'granularity', payload: granularity });
        Object.assign(modifiedParams, {
          [METRIC_KEY_MAP.rangeTime.granularity]: granularity,
          // 切换粒度的时候，需要清空折线图的选中
          [METRIC_KEY_MAP.selectedList]: [],
        });
      }

      if (dataSourceSingle?.value) {
        filterDispatch({ type: 'dataSourceSingle', payload: dataSourceSingle });
        Object.assign(modifiedParams, {
          dataSourceSingle: dataSourceSingle.value,
        });
      }
    } else {
      const targetKey = METRIC_KEY_MAP[type];
      filterDispatch({ type, payload });
      Object.assign(modifiedParams, {
        [targetKey]: payload,
      });
    }
    tableActionDispatch({
      type: 'pageIndex',
      payload: 1,
    });
    modifiedParams = Object.assign(payload, modifiedParams);
    initial({
      modifiedParams: {
        ...modifiedParams,
        page: 1,
      },
      init: true,
    });
  };

  const handlePagingChange = (e: any) => {
    if (filter.dimension === 'operation') return pageable;
    const { pageIndex, pageSize } = e;
    tableActionDispatch({
      type: 'pageIndex',
      payload: pageIndex,
    });
    tableActionDispatch({
      type: 'pageSize',
      payload: pageSize,
    });
    getTableData({
      modifiedParams: {
        order: tableAction.sortOrder,
        order_by: tableAction.sortKey,
        page: pageIndex,
        page_size: pageSize,
      },
    });
    // initial({
    //   modifiedParams: {
    //     page: pageIndex,
    //     page_size: pageSize,
    //   }
    // })
  };

  const handleSortChange = (e: any) => {
    const { key, sortVal } = e;
    const _sortKey = key.slice(0, key.length - 2);
    const isRatio = key.slice(key.length - 1) === '1';
    tableActionDispatch({
      type: 'sortKey',
      payload: isRatio ? `${_sortKey}_ratio` : _sortKey,
    });
    tableActionDispatch({
      type: 'sortKeyLabel',
      payload: key,
    });
    tableActionDispatch({
      type: 'sortOrder',
      payload: sortVal,
    });
    tableActionDispatch({
      type: 'pageIndex',
      payload: 1,
    });
    if (filter.dimension === 'operation') {
      const externalData = getSortedTableData({
        list: JSON.parse(JSON.stringify(tableData)),
        sortKey: key,
        sortOrder: sortVal,
        sortPosition: key.match('date') ? 'main' : 'other',
        matchKey: key.match('date') ? 'name' : '',
        valueType: key.match('date') ? 'date' : 'other',
        pageConfig: curPageConfig,
      });
      initial({
        isExternal: true,
        externalData,
        modifiedParams: {
          order: sortVal,
          order_by: _sortKey,
        },
      });
    } else {
      initial({
        modifiedParams: {
          order: sortVal,
          order_by: isRatio ? `${_sortKey}_ratio` : _sortKey,
          page: 1,
        },
        init: true,
      });
    }
  };

  const getIsHideCol = (condStr: string) => {
    return condStr === genreType;
  };

  const handleDateColFormat = (title: string) => {
    switch (filter.granularity) {
      case 'monthly':
        return moment(title).format('YYYY-MM');
      // return moment(title).format('MMM, YYYY');
      default:
        return moment(title).format('YYYY-MM-DD');
      // return moment(title).format('MMM DD, YYYY');
    }
  };

  const handleGameItemClick = (type: string, item: any) => {
    const { id, name_en, name_abbr } = item;
    const linkUrl = MARKET_DIMENSION_CONFIG.common.getLinkUrl(
      filter.dimension,
      filter.marketType,
      {
        id,
        name_abbr,
        name: name_en,
      },
    );
    linkUrl && history.push(linkUrl);
  };

  // checkbox的回调
  const handleTableCheckboxChange = (list: any, status: string) => {
    const isMoreThanTen = (_list) => {
      return _list.length >= MAX_CHECKED;
    };
    let checked = list;
    switch (status) {
      case 'uncheck all':
        tableActionDispatch({ type: 'selectedList', payload: list });
        break;
      case 'check all':
        if (tableAction.selectedList) {
          const topTen = list.slice(0, MAX_CHECKED);
          checked = topTen;
          tableActionDispatch({ type: 'selectedList', payload: topTen });
        } else {
          tableActionDispatch({ type: 'selectedList', payload: list });
        }
        break;
      case 'uncheck':
        const removedArr = tableAction.selectedList?.filter(
          (item) => item?.name !== list.name,
        );
        checked = removedArr;
        tableActionDispatch({ type: 'selectedList', payload: removedArr });
        break;
      case 'check':
        if (isMoreThanTen(tableAction.selectedList)) return;
        const newArrt = tableAction.selectedList?.concat(list);
        checked = newArrt;
        tableActionDispatch({ type: 'selectedList', payload: newArrt });
        break;
      default:
        break;
    }
    getLineChartData({
      modifiedParams: {
        select_code: getCheckedCode(checked, {}),
        checkedList: checked,
      },
    });
  };
  /*
  const setRegionList = async (modifiedParams) => {
    const _dimens = modifiedParams?.data_tag || filter.dimension || 'operation';
    const _marketType = modifiedParams?.entity_type || filter.marketType || 'mobile';
    const getRegionParams = () => {
      if (_dimens === 'games') {
        return [{
          source: 'mscience',
        }, {
          source: 'newzoo',
        }]
      } else {
        return [{
          source: 'mscience',
          metric_type: 'digital-revenue'
        }]
      }
    }
    const result = _marketType === 'mobile'
      ? await getGenreProfileRegionAndCountriesListWhenTotalAndMobile({
          type: [{tabName: entityType}],
          item:[{tabName: ''}]
        })
      : await getGenreProfileRegionAndCountriesListWhenOther({
          filters: getRegionParams()
    })
    const regionList = regionFormatter(result?.data?.region_country || []);
    setRegionOpts(regionList);
  };
*/
  const setTableCheck = (list) => {
    // tableActionDispatch({ type: 'selectedList', payload: list.slice(0, 6) });
    tableActionDispatch({ type: 'SelectionAll', payload: list });
  };

  const setDefaultDate = ({
    startDate,
    endDate,
  }: {
    startDate: string;
    endDate: string;
  }) => {
    if (!filter.startDate && !filter.endDate) {
      filterDispatch({
        type: 'startDate',
        payload: getFirstDayOfMonth(startDate),
      });
      filterDispatch({ type: 'endDate', payload: getFirstDayOfMonth(endDate) });
    }
  };

  const getDefaultDateValueByType = (dateType: string) => {
    if (toDateTypeFilter[dateType] === 'month') {
      return [
        getFirstDayOfMonth(
          moment().subtract(12, 'month').endOf('month').format('YYYY-MM-DD'),
        ),
        getFirstDayOfMonth(
          moment().subtract(1, 'months').endOf('month').format('YYYY-MM-DD'),
        ),
      ];
    }
    return getDefulatTimeValueByDateType(dateType);
  };

  const renderTableData = (
    { tableList, checkList, cond },
    isOuter?: boolean,
  ) => {
    if (!tableList) return;
    let { startDate, endDate, total } = cond;
    if (!filter.startDate || (!filter.endDate && startDate && endDate)) {
      setDefaultDate({ startDate, endDate });
    }
    if (!total) {
      total = tableList.length;
    }
    if (!checkList) {
      checkList = tableList;
    }
    if (isOuter) {
      const formattedTableData =
        curPageConfig.tableFormatter({
          list: tableList,
          metricMap: curPageConfig?.tableMetricList,
          keyMap: curPageConfig?.keyMap(),
          entityType: filter.marketType,
          dimen: filter.dimension,
        }) || [];
      setTableData(formattedTableData);
    } else {
      setTableData(tableList);
      setTableCheck(checkList);
    }
    tableActionDispatch({ type: 'total', payload: total });
    loadingDispatch({ type: 'table', payload: false });
  };

  const backfillingAfterSearch = () => {
    if (cachedParams?.current?.select_code) {
      cachedParams.current.select_code = [];
    }
    tableActionDispatch({
      type: 'pageIndex',
      payload: originalPageData.page,
    });
    tableActionDispatch({ type: 'total', payload: originalPageData.total });
    renderTableData({
      tableList: originalTableData,
      checkList: tableAction.selectedList,
      cond: {
        total: originalPageData.total,
      },
    });
    setTableData(originalTableData);
    getTableData({
      modifiedParams: {
        order: tableAction.sortOrder,
        order_by: tableAction.sortKey,
        page: tableAction.pageIndex,
        page_size: tableAction.pageSize,
      },
    });
  };

  const getTableData = ({
    modifiedParams,
    init = false,
  }: {
    modifiedParams?: IStatsModifiedParams;
    init?: boolean;
  }): Promise<[]> => {
    loadingDispatch({ type: 'table', payload: true });
    return new Promise<[]>((resolve, reject) => {
      const base = getParams({ type: 'table', modifiedParams });

      const requestParams: any = translateStateParams({ ...base, genreType });
      setRequestParams(requestParams);
      if (!isEmpty(requestParams)) {
        const requestFn = translateStateListFunc(base);
        // 同样只在mobile生效
        requestFn(requestParams)
          .then(async (result) => {
            let { table = [], total = 0 } = result?.data || {};
            table = await translateStateResponse(
              base,
              requestParams,
              result?.data || [],
            );
            const _marketType = modifiedParams?.entity_type;
            // 有异步问题，所以直接使用传入的值获取参数
            const _curPageConfig = getCurPageConfig({
              marketType: _marketType,
              dimension: modifiedParams?.data_tag,
            });
            if (base?.data_tag === 'operation') {
              const list = result?.data.list || [];
              let sum_revenue = 0;
              let sum_download = 0;
              function getData(keys: string) {
                const lineData = list.map((item: any) => {
                  item.time = item.date;
                  item.value = item.metrics[keys];
                  if (keys.indexOf('download') > -1) {
                    sum_download += item.value;
                  } else {
                    sum_revenue += item.value;
                  }
                  return item;
                });
                return JSON.parse(JSON.stringify(lineData)).sort(
                  (a: any, b: any) =>
                    new Date(b.time).valueOf() - new Date(a.time).valueOf(),
                );
              }
              const formattedLineData: any = {
                revenue: [
                  {
                    name: locale === 'zh-CN' ? '收入（美元）' : 'Revenue($)',
                    id: 'dbi_revenue_$',
                    nameEn: 'Revenue($)',
                    nameCn: '收入（美元）',
                    history: getData(`${base.dataSourceSingle}_revenue`),
                    cover: '',
                  },
                  {
                    name: locale === 'zh-CN' ? '下载' : 'Downloads',
                    id: 'dbi_downloads',
                    nameEn: 'Downloads',
                    nameCn: '下载',
                    history: getData(`${base.dataSourceSingle}_download`),
                    cover: '',
                  },
                ],
              };
              setOriginalLineData(formattedLineData);
              setLineChartData(formattedLineData);
              setLineChartSumData(
                getLineTotalText({
                  resultData: { sum_revenue, sum_download },
                  config: _curPageConfig?.lineTotalText,
                }),
              );
              loadingDispatch({ type: 'lineChart', payload: false });
            }
            const { start_date, end_date } = result?.req || {};
            const formattedTableData =
              _curPageConfig.tableFormatter({
                list: table || [],
                metricMap: _curPageConfig?.tableMetricList,
                keyMap: _curPageConfig?.keyMap(),
                entityType: _marketType || filter.marketType,
                dimen: modifiedParams?.data_tag || filter.dimension,
              }) || [];
            if (
              Array.isArray(requestParams?.select_code) &&
              requestParams?.select_code.length === 0
            ) {
              setOriginalTableData(formattedTableData);
              setOriginalPageData({
                total,
                pageSize: modifiedParams?.page_size || 1,
                page: modifiedParams?.page || 50,
              });
            }
            const checkedList = table?.slice(0, 6) || [];
            if (init) {
              tableActionDispatch({
                type: 'selectedList',
                payload: checkedList,
              });
              resolve(checkedList);
            } else {
              resolve([]);
            }
            renderTableData({
              tableList: formattedTableData,
              checkList: formattedTableData,
              cond: {
                startDate: start_date,
                endDate: end_date,
                total,
              },
            });
          })
          .catch(() => {
            renderTableData({
              tableList: [],
              checkList: [],
              cond: {},
            });
            loadingDispatch({ type: 'table', payload: false });
            reject([]);
          });
      } else {
        renderTableData({
          tableList: [],
          checkList: [],
          cond: {},
        });
        loadingDispatch({ type: 'table', payload: false });
        reject([]);
      }
    });
  };

  const changeLineChartDataByLocale = (_locale: string) => {
    loadingDispatch({ type: 'lineChart', payload: true });
    const _tmpObj: any = {};
    Object.keys(originalLineData).forEach((key: string) => {
      _tmpObj[key] = originalLineData[key].map((item) => ({
        ...item,
        name:
          filter.dimension === 'operation' && item?.nameKey
            ? formatMessage({
              id: item?.nameKey || '',
            })
            : _locale === 'zh-CN'
              ? item.nameCn
              : item.nameEn,
      }));
    });
    setLineChartData(_tmpObj);
    loadingDispatch({ type: 'lineChart', payload: false });
  };

  const getLineChartData = async ({
    modifiedParams,
  }: {
    modifiedParams?: IStatsModifiedParams;
  }) => {
    const base = getParams({ type: 'line', modifiedParams });
    if (base.data_tag === 'operation') return;
    loadingDispatch({ type: 'lineChart', payload: true });
    try {
      const requestParams: any = translateStateParams(
        { ...base, genreType },
        'line',
      );
      const result = await getGenreProfileStatsLine(requestParams);
      const { list = [] } = result?.data || {};
      const options = getMetricSeg(
        curPageConfig?.lineMetricList,
        base.granularity,
      );
      const formattedLineData =
        MARKET_DIMENSION_CONFIG.common.lineChartFormatter({
          list: list || [],
          metricMap: options,
          isCn: locale === 'zh-CN',
          sourceMetrics: Object.keys(requestParams.source_metrics)[0],
          genre_tab: requestParams.genre_tab,
          dealMap: {
            data: genreOptions,
            fn: dealMap,
            type: genreSource, // requestParams.genre_source || filter.genreSource || 'ieg',
          },
          keys:
            base.data_tag === 'games'
              ? 'game_id'
              : base.data_tag === 'companies'
              ? 'publisher_id'
              : base.data_tag === 'countries'
                  ? 'market'
              : '',
          checkList: base.checkedList,
        });
      setOriginalLineData(formattedLineData);
      setLineChartData(formattedLineData);
      loadingDispatch({ type: 'lineChart', payload: false });
    } catch (error) {
      setLineChartData({});
      loadingDispatch({ type: 'lineChart', payload: false });
    }
  };

  const getLineTotalText = ({ resultData, config }) => {
    return (
      config?.map((item) => ({
        ...item,
        value: resultData[item.key],
      })) || []
    );
  };

  // const getDataWhenOpera = ({
  //   isExternal,
  //   externalData,
  //   modifiedParams,
  // }: {
  //   isExternal?: boolean;
  //   externalData?: any;
  //   modifiedParams?: IStatsModifiedParams;
  // }) => {
  //   loadingDispatch({ type: 'table', payload: true });
  //   loadingDispatch({ type: 'lineChart', payload: true });
  //   const _marketType = modifiedParams?.entity_type;
  //   // 有异步问题，所以直接使用传入的值获取参数
  //   const _curPageConfig = getCurPageConfig({
  //     marketType: _marketType,
  //     dimension: modifiedParams?.data_tag,
  //   });
  //   const getTableAndLineData = ({ ori: originalData, sorted: sortedData, isExternal }: any) => {
  //     const formattedTableData =
  //       _curPageConfig.tableFormatter({
  //         list: sortedData || [],
  //         metricMap: _curPageConfig?.tableMetricList,
  //         keyMap: _curPageConfig?.keyMap(),
  //         entityType: _marketType || filter.marketType,
  //         dimen: modifiedParams?.data_tag || filter.dimension,
  //       }) || [];
  //     // 23.10.11 产品赵漫要求：折线图不根据排序变化，保持日期从左到右是从小到大
  //     if (isExternal) {
  //       return {
  //         table: formattedTableData || [],
  //         lineChart: lineChartData || [],
  //       };
  //     }
  //     // 折线图不跟随排序，只根据原始数据的日期展示
  //     const formattedLineChartData =
  //       _curPageConfig.lineFormatter({
  //         list: originalData || [],
  //         keyMap: _curPageConfig?.keyMap(),
  //         metricMap: _curPageConfig?.lineMetricList,
  //         localeFn: formatMessage,
  //         timeType: modifiedParams?.granularity || filter.granularity,
  //       }) || [];
  //     setOriginalLineData(formattedLineChartData);
  //     return {
  //       table: formattedTableData || [],
  //       lineChart: formattedLineChartData || [],
  //     };
  //   };
  //   if (isExternal) {
  //     try {
  //       const { table, lineChart } = getTableAndLineData({
  //         sorted: externalData,
  //         isExternal,
  //       });
  //       const startNum = (tableAction.pageIndex - 1) * tableAction.pageSize;
  //       renderTableData({
  //         tableList: table,
  //         checkList: table.slice(startNum, startNum + tableAction.pageSize - 1),
  //         cond: {
  //           total: table?.length,
  //         },
  //       });
  //       setLineChartData(lineChart);
  //       loadingDispatch({ type: 'lineChart', payload: false });
  //     } catch (error) {
  //       loadingDispatch({ type: 'table', payload: false });
  //       loadingDispatch({ type: 'lineChart', payload: false });
  //     }
  //     return;
  //   }
  //   getGenreProfileStatsLineData(getParams({ type: 'operation', modifiedParams }))
  //     .then((result) => {
  //       const resultData = result?.data || {};
  //       const { start_date, end_date } = result?.req || {};
  //       const sortedTableData = getSortedTableData({
  //         list: JSON.parse(JSON.stringify(resultData.trend || [])),
  //         sortKey: 'date_0',
  //         sortOrder: 'desc',
  //         sortPosition: 'main',
  //         matchKey: 'name',
  //         valueType: 'date',
  //         pageConfig: _curPageConfig,
  //       });
  //       const { table, lineChart } = getTableAndLineData({
  //         ori: resultData.trend,
  //         sorted: sortedTableData,
  //       });
  //       renderTableData({
  //         tableList: table,
  //         checkList: table,
  //         cond: {
  //           startDate: start_date,
  //           endDate: end_date,
  //           total: table?.length,
  //         },
  //       });

  //       setLineChartData(lineChart);
  //       loadingDispatch({ type: 'lineChart', payload: false });
  //     })
  //     .catch(() => {
  //       renderTableData({
  //         tableList: [],
  //         checkList: [],
  //         cond: {},
  //       });
  //       setLineChartSumData([]);
  //       loadingDispatch({ type: 'table', payload: false });
  //       loadingDispatch({ type: 'lineChart', payload: false });
  //     });
  // };

  // 判断是否要重新请求region数据
  const isNeedSetRegion = (modifiedParams?: IStatsModifiedParams) => {
    const dimensNotGamesMap: any = ['operation', 'countries', 'companies'];
    const _lastDimens = lastDimens.current;
    const _lastMarketType = lastMarketType.current;
    const modifyDimens = modifiedParams?.data_tag;
    const modifyMarketType = modifiedParams?.entity_type;
    if (!_lastDimens) {
      lastDimens.current = 'operation';
    } else if (modifyDimens) {
      lastDimens.current = modifyDimens;
    }
    if (!_lastMarketType) {
      lastMarketType.current = entityType;
    } else if (modifyMarketType) {
      lastMarketType.current = modifyMarketType;
    }
    // 初始化进来要加载数据
    return (
      !_lastDimens ||
      !_lastMarketType ||
      // 市场发生变化要加载数据
      (modifyMarketType && modifyMarketType !== _lastMarketType) ||
      // 从非games跳到games
      (dimensNotGamesMap.includes(_lastDimens) && modifyDimens === 'games') ||
      // 从games跳到非games
      (_lastDimens === 'games' && dimensNotGamesMap.includes(modifyDimens))
    );
  };

  const reset = () => {
    setTableData([]);
    setLineChartData({});
    setLineChartSumData([]);
  };

  const handleDefaultSortKey = ({
    modifiedParams,
  }: {
    modifiedParams?: IStatsModifiedParams;
  }) => {
    const _dimens = modifiedParams?.data_tag || filter.dimension;
    let orderBy = 'date';
    if (_dimens === 'operation') {
      tableActionDispatch({ type: 'sortKey', payload: 'date' });
      tableActionDispatch({ type: 'sortKeyLabel', payload: 'date_0' });
    } else {
      const _curPageConfig = getCurPageConfig({
        marketType: modifiedParams?.entity_type,
        dimension: modifiedParams?.data_tag,
        ...modifiedParams,
        dataSourceSingle: modifiedParams?.dataSourceSingle?.value,
      });
      orderBy = _curPageConfig?.tableMetricList[0]?.metric;
      tableActionDispatch({ type: 'sortKey', payload: orderBy });
      tableActionDispatch({ type: 'sortKeyLabel', payload: `${orderBy}_0` });
    }
    return {
      order: 'desc',
      order_by: orderBy,
    };
  };

  /**
   * 初始化方法
   * @param param0 isExternal 是否有附加属性，用于前端排序分页等
   * @param param0 externalData 附加数据，表格、折线图都是拿这个数据进行计算
   * @param param0 modifiedParams 修改的参数，同步传入，忽略异步影响
   */
  const initial = async ({
    isExternal,
    externalData,
    modifiedParams,
    init = false,
  }: {
    isExternal?: boolean;
    externalData?: any;
    modifiedParams?: IStatsModifiedParams;
    init?: boolean;
  }) => {
    reset();
    const sortAdd = isExternal
      ? {}
      : modifiedParams?.hasOwnProperty('order_by')
        ? {}
        : handleDefaultSortKey({
          modifiedParams: { ...filter, ...modifiedParams },
        });

    let newModifiedParams = Object.assign(
      {
        ...filter,
        dataSourceSingle:
          filter.dataSourceSingle?.value || filter.dataSourceSingle,
      },
      modifiedParams,
      sortAdd,
    );

    const _dimens = newModifiedParams?.data_tag || filter.dimension;
    // if (isNeedSetRegion(newModifiedParams)) {
    //   // setRegionList(newModifiedParams);
    //   filterDispatch({ type: 'markets', payload: ['global'] });
    //   filterDispatch({ type: 'regions', payload: ['global'] });
    //   Object.assign(newModifiedParams, {
    //     regions: ['global'],
    //   });
    // }
    // if (_dimens === 'operation') {
    //   getDataWhenOpera({ isExternal, externalData, modifiedParams: newModifiedParams });
    // } else {

    try {
      loadingDispatch({ type: 'lineChart', payload: true });
      // 添加开始结束时间兜底
      if (!newModifiedParams?.startDate && !newModifiedParams?.endDate) {
        const startDateTemp = getDefaultDateValueByType(
          newModifiedParams?.granularity || filter?.granularity,
        )?.[0];
        const endDateTemp = getDefaultDateValueByType(
          newModifiedParams?.granularity || filter?.granularity,
        )?.[1];
        newModifiedParams = {
          ...newModifiedParams,
          start_date: startDateTemp,
          end_date: endDateTemp,
        };
        filterDispatch({ type: 'startDate', payload: startDateTemp });
        filterDispatch({ type: 'endDate', payload: endDateTemp });
      }
      const res = await getTableData({
        modifiedParams: newModifiedParams,
        init,
      });
      getLineChartData({
        modifiedParams: { ...newModifiedParams, checkedList: res },
      });
    } catch (error) {
      console.log(error);
    }
    // }
  };

  // 根据hideCond判断字段是否展示
  const getMetricShow = (metricItem: any, granularity?: string) => {
    const { hideCond } = metricItem;
    if (!hideCond) return true;
    const conds = hideCond.split(',') || [];
    const find = conds.find(
      (cond: string) =>
        GRANULARITY_MAP[granularity || filter.granularity || 'monthly'] ===
        cond,
    );
    return !find;
  };

  const getMetricSeg = (_metricList: any[], granularity?: string) => {
    return _metricList.filter((item) => getMetricShow(item, granularity));
  };

  const getHtmlContent = () => {
    const getChart = () => (
      <StatsLineChart
        isLoading={loading.lineChart}
        metricSegments={getMetricSeg(curPageConfig?.lineMetricList)}
        metricList={lineChartSumData}
        metricInfo={curPageConfig?.lineMetricInfo}
        entityType={filter.marketType}
        lineChartData={lineChartData}
      />
    );
    const getSearch = () => {
      return (
        !loading.table && (
          <GenreProfileSearch
            marketType={filter.marketType}
            dimension={filter.dimension}
            getPageParams={getParamsForSearch}
            searchApi={getGenreProfileStatsTableData}
            pageLogInfo={pageLogInfo}
            renderTableData={renderTableData}
            getTableData={getTableData}
            resetOuterTableData={backfillingAfterSearch}
          />
        )
      );
    };
    const getTable = () => {
      if (filter.dimension === 'operation' && loading.table) {
        // operation页loading只展示一个
        return <></>;
      }
      return (
        <DynamicTable
          isDetail={true}
          tableData={tableData}
          isLoading={loading.table}
          entityType={filter.marketType}
          tableSelected={tableAction.selectedList}
          tableSelectedAll={tableAction.SelectionAll}
          total={tableAction.total}
          isNotPageableAuto={filter.dimension !== 'operation'}
          sortKey={tableAction.sortKeyLabel}
          sortOrder={tableAction.sortOrder}
          pageSize={tableAction.pageSize}
          pageIndex={tableAction.pageIndex}
          scrollMinWidth={tableScrollWidth}
          keyMap={curPageConfig.keyMap()}
          getDateColFormat={handleDateColFormat}
          metricCol={getMetricSeg(curPageConfig.tableMetricList)}
          firstCol={curPageConfig.tableFirstCol}
          specialCol={curPageConfig.tableSpecialCol}
          getIsHideCol={getIsHideCol}
          pageSizeOptions={[10, 20, 50]}
          onPagingChange={handlePagingChange}
          onSortChange={handleSortChange}
          onTableCheckboxChange={handleTableCheckboxChange}
          onGameItemClick={handleGameItemClick}
          dealMap={{
            data: genreOptions,
            fn: dealMap,
            type: genreSource,
          }}
        />
      );
    };
    if (filter.dimension === 'operation')
      return (
        <CardSlotV2
          title={formatMessage({ id: curPageConfig?.title })}
          className="dbi-stats-data"
          iconList={[]}
        >
          {/* {getChart()} */}
          {getTable()}
        </CardSlotV2>
      );
    return (
      <>
        {/* <CardSlotV2
          title={formatMessage({ id: curPageConfig?.title })}
          className="dbi-stats-data"
          iconList={[]}
        >
          {getChart()}
        </CardSlotV2> */}
        {/* {getSearch()} */}
        <CardSlotV2 iconList={[]}>{getTable()}</CardSlotV2>
      </>
    );
  };

  useEffect(() => {
    const pageKey = `${filter.marketType}_${filter.dimension}`;
    const pageLogConfigKey = PAGE_LOG_CONFIG_KEYS[pageKey];
    const pageLogConfig = dbiPageLogMes[pageLogConfigKey];
    if (!pageLogConfig) return;
    buriedPointPage(pageLogConfig);
    setPageLogInfo(pageLogConfig);
  }, [filter.marketType, filter.dimension]);

  useEffect(() => {
    // 转移国家中文
    changeLineChartDataByLocale(locale);
  }, [locale]);

  useEffect(() => {
    if (filter?.dimension === 'countries') {
      tableAction.selectedList.map((team: any) => {
        team.name = team.name_abbr;
        team.name_cn = findCountryObj(team.name_abbr)?.nameCh;
        team.name_en = findCountryObj(team.name_abbr)?.nameEn;
      });
    }
  }, [tableAction.selectedList]);

  useEffect(() => {
    if (!genreOptions) return;
    initial({ init: true });
  }, [genreOptions]);

  useEffect(() => {
    const type = filter.marketType;
    if ((type === 'pc' || type === 'console') && !filterLoadingPC) {
      setRegionOpts(regionOptionsPC || []);
    }
    if (type === 'mobile' && !filterLoadingMobile) {
      setRegionOpts(regionOptionsMobile || []);
    }
    // 转移国家中文
    if (filter?.dimension === 'countries') {
      tableData.map((team: any) => {
        team.name = team.name_abbr;
        team.name_cn = findCountryObj(team.name_abbr)?.nameCh;
        team.name_en = findCountryObj(team.name_abbr)?.nameEn;
      });
    }
  }, [
    tableData,
    filter.marketType,
    filterLoadingPC,
    filterLoadingMobile,
    regionOptionsMobile,
    regionOptionsPC,
  ]);

  useEffect(() => {
    // 修复在升序情况下，切换标签时依旧是升序
    tableActionDispatch({ type: 'sortOrder', payload: 'desc' });
  }, [filter.marketType]);

  const downloadPageFun = async (
    downloadParams: any,
    downloadMetrics: any,
    dataAmount: any,
  ) => {
    const newParams = {
      ...downloadParams.requestParams,
      page_size: dataAmount,
    };

    if (Array.isArray(downloadMetrics)) {
      const isOperationData = downloadMetrics?.includes('operation');
      const isByGames = downloadMetrics?.includes('games');
      const byCountries = downloadMetrics?.includes('countries');
      const isByCompanies = downloadMetrics?.includes('companies');

      const fetchList = [
        isOperationData
          ? getGenreProfileStatsLine({
            ...newParams,
            group_by: ['date'],
          })
          : '',
        isByGames
          ? getGenreProfileStatsLine({
            ...newParams,
            group_by: ['game_id'],
          })
          : '',
        byCountries
          ? getGenreProfileStatsLine({
            ...newParams,
            group_by: ['market'],
          })
          : '',
        isByCompanies
          ? getGenreProfileStatsLine({
            ...newParams,
            group_by: ['publisher_id'],
          })
          : '',
      ];

      try {
        const [
          operationDataRes = {},
          byGamesRes = {},
          byCountriesRes = {},
          byCompaniesRes = {},
        ] = await Promise.all(fetchList);

        const resParams = {
          ...downloadParams,
        };
        if (
          !isEmpty(operationDataRes) &&
          Array.isArray(operationDataRes?.data?.list) &&
          operationDataRes?.data?.list?.length > 0
        ) {
          const resListTemp = [...operationDataRes?.data?.list];
          const resList = resListTemp?.sort((a: any, b: any) => {
            const timeA = new Date(a.date).getTime();
            const timeB = new Date(b.date).getTime();
            return timeB - timeA;
          });
          const timeStart = resList?.[0]?.date;
          const timeEnd = resList?.[resList?.length - 1]?.date;
          const [startTime, endTime] =
            timeStart < timeEnd ? [timeStart, timeEnd] : [timeEnd, timeStart];
          resParams.operationDataStart = startTime;
          resParams.operationDataEnd = endTime;
        }
        if (!isEmpty(byGamesRes)) {
          const resList = byGamesRes?.data?.list;
          resParams.gamesIdList = resList?.map((item: any) => item?.game_id);
        }
        if (!isEmpty(byCountriesRes)) {
          const resList = byCountriesRes?.data?.list;
          resParams.countriesList = resList?.map((item: any) => item?.market);
        }
        if (!isEmpty(byCompaniesRes)) {
          const resList = byCompaniesRes?.data?.list;
          resParams.companiesList = resList?.map(
            (item: any) => item?.publisher_id,
          );
        }
        return resParams;
      } catch (error) {
        console.error(error);
      } finally {
      }
    }
  };

  return (
    <div>
      <Filters
        isMarketSegmentHide={isMarketSegmentHide}
        filterValues={filter}
        genreName={genreName}
        genreType={genreType}
        metricsList={metricsList}
        order={tableAction.sortOrder}
        orderBy={tableAction.sortKey}
        genreSource={transGenreSource(genreSource)}
        regionOpts={regionOpts}
        total={tableAction.total}
        onFilterChange={handleFilterChange}
        entityType={leftSegment}
        defaultValue={filter}
        requestParams={requestParams}
        downloadPageFun={downloadPageFun}
      />
      <CardSlotV2
        title={formatMessage({ id: curPageConfig?.title })}
        className="dbi-stats-data"
        iconList={[]}
      >
        <StatsLineChart
          isLoading={loading.lineChart}
          metricSegments={getMetricSeg(curPageConfig?.lineMetricList)}
          metricList={lineChartSumData}
          metricInfo={curPageConfig?.lineMetricInfo}
          entityType={filter.marketType}
          lineChartData={lineChartData}
          dateType={filter.granularity || 'monthly'}
          dataSource={filter.dataSourceSingle.value}
          regionOptionsMobile={regionOptionsMobile}
        />
      </CardSlotV2>
      {filter?.dimension !== 'operation' && (
        <GenreProfileSearch
          marketType={filter.marketType}
          dimension={filter.dimension}
          getPageParams={getParamsForSearch}
          searchApi={getGenreProfileStatsTableData}
          pageLogInfo={pageLogInfo}
          renderTableData={renderTableData}
          getTableData={getTableData}
          resetOuterTableData={backfillingAfterSearch}
          genreName={genreName}
          loadingDispatch={loadingDispatch}
          tableActionDispatch={tableActionDispatch}
        />
      )}
      {getHtmlContent()}
    </div>
  );
}
